/**
 * Web Component: <exp-pagination>
 * Coordinates with <exp-blog-grid> and <exp-blog-sort>:
 * - Slices and displays only the active page items
 * - Dynamically calculates total pages from total articles and posts-per-page
 * - Resets to page 1 when sort order changes
 * - Handles prev/next arrows and direct page clicks
 */
class ExpPagination extends HTMLElement {
  constructor() {
    super();
    this.list = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.postsPerPage = 4;
    this.grid = null;
    this.onSortChange = this.onSortChange.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('.exp-pagination__list');
    this.initGridSync();
    this.bindEvents();

    window.addEventListener('exp:sort-change', this.onSortChange);
    window.addEventListener('exp:page-change', this.onPageChange);
  }

  disconnectedCallback() {
    window.removeEventListener('exp:sort-change', this.onSortChange);
    window.removeEventListener('exp:page-change', this.onPageChange);
  }

  initGridSync() {
    this.grid = document.querySelector('.exp-blog-grid__list, [data-exp-blog-grid]');
    if (!this.grid) return;

    this.postsPerPage = parseInt(this.grid.getAttribute('data-posts-per-page') || '4', 10);
    const items = this.grid.querySelectorAll('.exp-blog-grid__item, .blog-post-item');
    const totalItems = items.length;

    this.totalPages = Math.max(1, Math.ceil(totalItems / this.postsPerPage));
    this.setAttribute('data-total-pages', this.totalPages.toString());

    // If total pages <= 1, hide pagination completely
    if (this.totalPages <= 1) {
      this.style.display = 'none';
      return;
    } else {
      this.style.display = '';
    }

    // Read initial page from URL (?page=X)
    const params = new URLSearchParams(window.location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    this.currentPage = Math.min(Math.max(1, pageParam), this.totalPages);

    this.renderButtons();
    this.updateGridVisibility(false);
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.exp-pagination__link[data-page]');
      if (link) {
        e.preventDefault();
        const targetPage = parseInt(link.getAttribute('data-page'), 10);
        if (targetPage !== this.currentPage) {
          this.goToPage(targetPage, true);
        }
        return;
      }

      const nextBtn = e.target.closest('[data-exp-pagination-next]');
      if (nextBtn && this.currentPage < this.totalPages) {
        e.preventDefault();
        this.goToPage(this.currentPage + 1, true);
        return;
      }

      const prevBtn = e.target.closest('[data-exp-pagination-prev]');
      if (prevBtn && this.currentPage > 1) {
        e.preventDefault();
        this.goToPage(this.currentPage - 1, true);
        return;
      }
    });
  }

  onSortChange() {
    // When sort changes (Newest / Oldest), reset navigation back to page 1
    this.goToPage(1, false);
  }

  onPageChange(e) {
    if (e.detail && e.detail.page) {
      this.currentPage = e.detail.page;
      this.renderButtons();
    }
  }

  goToPage(pageNum, shouldScroll = true) {
    if (pageNum < 1 || pageNum > this.totalPages) return;
    this.currentPage = pageNum;

    // Update URL query string without full reload
    const url = new URL(window.location);
    url.searchParams.set('page', this.currentPage.toString());
    window.history.replaceState({}, '', url);

    this.renderButtons();
    this.updateGridVisibility(shouldScroll);
  }

  updateGridVisibility(shouldScroll = true) {
    if (!this.grid) {
      this.grid = document.querySelector('.exp-blog-grid__list, [data-exp-blog-grid]');
    }
    if (!this.grid) return;

    const items = Array.from(this.grid.querySelectorAll('.exp-blog-grid__item, .blog-post-item'));
    const startIdx = (this.currentPage - 1) * this.postsPerPage;
    const endIdx = startIdx + this.postsPerPage;

    items.forEach((item, idx) => {
      if (idx >= startIdx && idx < endIdx) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });

    if (shouldScroll) {
      const gridSection = this.grid.closest('.exp-blog-grid') || this.grid;
      const headerOffset = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '80', 10);
      const targetPos = gridSection.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20;
      window.scrollTo({
        top: Math.max(0, targetPos),
        behavior: 'smooth'
      });
    }
  }

  renderButtons() {
    if (!this.list) return;

    // If only 1 page, hide entire component
    if (this.totalPages <= 1) {
      this.style.display = 'none';
      return;
    } else {
      this.style.display = '';
    }

    let html = '';

    // Prev Arrow (←) - STRICTLY ONLY RENDER IF CLICKABLE (currentPage > 1)
    if (this.currentPage > 1) {
      const prevPage = this.currentPage - 1;
      html += `
        <li class="exp-pagination__item exp-pagination__item--prev">
          <a
            href="?page=${prevPage}"
            class="exp-pagination__arrow exp-pagination__arrow--prev"
            data-exp-pagination-prev
            aria-label="Previous page"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20 12H6M12 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </li>
      `;
    }

    // Number Buttons
    for (let i = 1; i <= this.totalPages; i++) {
      const isActive = i === this.currentPage;
      html += `
        <li class="exp-pagination__item">
          <a
            href="?page=${i}"
            class="exp-pagination__link${isActive ? ' is-active' : ''}"
            data-page="${i}"
            ${isActive ? 'aria-current="page"' : ''}
          >
            ${i}
          </a>
        </li>
      `;
    }

    // Next Arrow (→) - STRICTLY ONLY RENDER IF CLICKABLE (currentPage < totalPages)
    if (this.currentPage < this.totalPages) {
      const nextPage = this.currentPage + 1;
      html += `
        <li class="exp-pagination__item exp-pagination__item--next">
          <a
            href="?page=${nextPage}"
            class="exp-pagination__arrow exp-pagination__arrow--next"
            data-exp-pagination-next
            aria-label="Next page"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M4 12h14M12 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </li>
      `;
    }

    this.list.innerHTML = html;
  }
}

if (!customElements.get('exp-pagination')) {
  customElements.define('exp-pagination', ExpPagination);
}
